import { CJEvent, CJEventStatus } from 'types/event';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import useNotification from 'containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

interface SetCJSeanceStatusParams {
  date: Date;
  status: CJEventStatus;
}

export function useSetCJSeanceStatus(disableUndoRedo?: boolean) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();

  const queryClient = useQueryClient();
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { t } = useTranslation(['cahierJournal']);
  const { mutateAsync } = useMutation({
    mutationFn: ({ date, status }: SetCJSeanceStatusParams) => {
      const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/cahier-journal/seances/status`;
      return request(requestURL, {
        method: 'PATCH',
        body: {
          date: format(date, 'fr-FR'),
          status,
        },
      });
    },
    onMutate: ({ date, status }) => {
      if (!disableUndoRedo) {
        addToPast({
          action: 'UPDATE_STATUS',
          payload: {
            status: status === 'done' ? 'open' : 'done',
            date,
          },
        });
      }
      queryClient.setQueryData<CJEvent[]>(
        [schoolyear.id, school.id, EVENTS_KEY, format(date, 'fr-FR')],
        old => (old || []).map(ev => ({ ...ev, status })),
      );
    },
    onError: () => {
      notifyError({
        title: t('cahierJournal:messages.updateError_other'),
      });
    },
  });
  return mutateAsync;
}
